import React, { useCallback, useState } from 'react'
import { Form, Input, Button, Typography, App } from 'antd'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Loader } from 'lucide-react'
import { AuthCard } from '../../components/Auth/AuthCard'
import { PasswordRequirements } from './PasswordRequirements'
import { useApi } from '../../services/ApiContext'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'

const { Text } = Typography

export const AcceptInvitationPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { message } = App.useApp()
  const [captcha, setCaptcha] = useState<null | string>(null)

  // Get invitation details from URL params
  const email = decodeURIComponent(searchParams.get('email') ?? '')
  const organisationName = decodeURIComponent(searchParams.get('organisationName') ?? '')
  const inviteToken = searchParams.get('inviteToken')
  const api = useApi()

  const handleSubmit = async (values: { password: string }) => {
    if (!captcha || !inviteToken) {
      return
    }
    try {
      setLoading(true)
      await api.acceptSignupInvite(values.password, inviteToken, captcha)
      navigate('/app/documents')
    } catch (error) {
      alert(error)
      console.error(error)
      message.error('Failed to activate account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const onVerify = useCallback((token: string | null) => {
    setCaptcha(token)
  }, [])

  const [password, setPassword] = useState('')

  if (!email || !inviteToken || !organisationName) {
    return (
      <AuthCard>
        <div className="text-center">
          <Text type="danger" className="block mb-4">
            Invalid invitation link. Please check your email and try again.
          </Text>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">Welcome to SprintSpec</h1>
        <Text type="secondary" className="block">
          You've been invited to join {organisationName}. Please set up your
          password to continue.
        </Text>
      </div>

      <GoogleReCaptcha onVerify={onVerify} />

      <Form validateTrigger="onSubmit" layout="vertical" onFinish={handleSubmit}>
        <Form.Item initialValue={email} name="email">
          <Input
            disabled
            name="email"
            placeholder="Enter Your Email"
            className="h-11"
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[
            { required: true, message: 'Please enter a password' },
            { min: 8, message: 'Password must be at least 8 characters' },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
              message: 'Password must include uppercase, lowercase, and numbers'
            }
          ]}
          hasFeedback={false}
        >
          <Input.Password
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            name="password"
            placeholder="Create password"
            className="h-11"
            iconRender={(visible) =>
              visible ? (
                <Eye className="h-4 w-4 text-gray-400" />
              ) : (
                <EyeOff className="h-4 w-4 text-gray-400" />
              )
            }
          />
        </Form.Item>

        <PasswordRequirements password={password} />

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          disabled={loading}
          className="h-11"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader className="h-4 w-4 animate-spin" />
              Setting Up Account...
            </span>
          ) : (
            'Set Password & Continue'
          )}
        </Button>
      </Form>

      <div className="text-center mt-6">
        <Text type="secondary" className="text-xs">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-gray-600 hover:text-gray-900">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-gray-600 hover:text-gray-900">
            Privacy Policy
          </Link>
        </Text>
      </div>
    </AuthCard>
  )
}
